import React from "react";
import { Link } from "react-router-dom";
import CodeBlock, { CodeText } from "../../components/CodeBlock";
import { Tab } from "../../components/CodeBlock/CodeBlock";
import useScrollToHash from "../../utils/scrollToHash";
import SectionLinkCopy from "../../components/SectionLinkCopy";

const Function = () => {
    useScrollToHash();
    return (
        <>
            <section className="min-h-screen bg-secondary-dark flex flex-wrap pt-[14vh] pb-5 md:pt-[15vh] px-4 md:px-11 flex-col items-centers">
                <div className="text-secondary text-3xl md:text-6xl mx-auto">
                    Function
                </div>
                <section id="function_defining" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Function defining
                    <SectionLinkCopy sectionId="function_defining" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        Say goodbye to creating a new file for every single
                        function, introducing Function Defining. This feature
                        allows you to define as many functions as you want in a
                        single file. Any capital letter (which is invalid for
                        mcfunction name) will automatically be turned into
                        lowercase, which means it is not case-sensitive. For
                        example, <code>deathMessage</code> is the same as{" "}
                        <code>deathmessage</code>.
                    </p>
                    <CodeBlock>
                        <CodeText type="keyword">function</CodeText>{" "}
                        <CodeText type="function">
                            folder_name.function_name
                        </CodeText>
                        () {"{"} <br />
                        <Tab />
                        say <CodeText type="string">"Code example 1"</CodeText>;
                        <br />
                        <Tab />
                        say <CodeText type="string">"Code example 2"</CodeText>;
                        <br />
                        {"}"}
                        <br />
                        <CodeText type="keyword">function</CodeText>{" "}
                        <CodeText type="function">function_name</CodeText>
                        () {"{"} <br />
                        <Tab />
                        say <CodeText type="string">"Code example 3"</CodeText>;
                        <br />
                        {"}"}
                    </CodeBlock>
                    <p>
                        <Tab />
                        However, there are limitations.{" "}
                        <span className="text-warning">
                            You are not allowed to create parameter(s) like this
                            example.
                        </span>
                    </p>
                    <CodeBlock>
                        <CodeText type="keyword">function</CodeText>{" "}
                        <CodeText type="function">invalidFunction</CodeText>(
                        <CodeText type="error">$parameter</CodeText>) {"{"}{" "}
                        <br />
                        <Tab />
                        say{" "}
                        <CodeText type="string">
                            "This will throw an error."
                        </CodeText>
                        ;<br />
                        {"}"}
                    </CodeBlock>
                </div>
                <section id="function_calling" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Function calling
                    <SectionLinkCopy sectionId="function_calling" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        Instead of using the <code>/function</code> command, you
                        can use parentheses to call a function. JMC will add a
                        namespace for you automatically. Like function defining,
                        the name is not case-sensitive.
                    </p>
                    <CodeBlock>
                        <CodeText type="function">
                            folder_name.function_name
                        </CodeText>
                        ();{" "}
                        <CodeText type="comment">
                            {"// function namespace:folder_name/function_name"}
                        </CodeText>
                        <br />
                        <CodeText type="function">function_name</CodeText>
                        ();
                        <br />
                        schedule function{" "}
                        <CodeText type="function">function_name</CodeText>
                        ()
                        {" <time> [append|replace];"}
                        <br />
                        schedule clear{" "}
                        <CodeText type="function">function_name</CodeText>
                        ();
                        <br />
                        <CodeText type="variable">$var</CodeText>{" "}
                        <CodeText type="operator">=</CodeText>{" "}
                        <CodeText type="function">
                            folder_name.function_name
                        </CodeText>
                        (); <br />
                        <CodeText type="keyword">if</CodeText> (
                        <CodeText type="function">function_name</CodeText>()){" "}
                        {"{"}
                        <CodeText type="operator">...</CodeText>
                        {"}"}
                    </CodeBlock>
                </div>
                <section id="anonymous_function" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Anonymous Function
                    <SectionLinkCopy sectionId="anonymous_function" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        The archnemesis of every coder is, indeed, naming
                        things. Why bother creating a whole new function just to
                        run a few commands as an entity? You are allowed to run
                        multiple commands in an execute chain in JMC.
                    </p>
                    <CodeBlock>
                        execute <CodeText type="operator">...</CodeText> run{" "}
                        {"{"}
                        <br />
                        <Tab />
                        say <CodeText type="string">"Code example 1"</CodeText>;
                        <br />
                        <Tab />
                        say <CodeText type="string">"Code example 2"</CodeText>;
                        <br />
                        {"}"}
                    </CodeBlock>
                    <p>
                        <Tab />
                        You can also do the same with schedule command. Note
                        that the function will lose its context such as the
                        entity running it like normal schedule command.
                    </p>
                    <CodeBlock>
                        schedule <CodeText type="number">{"<time>"}</CodeText>{" "}
                        [replace/append] {"{"}
                        <br />
                        <Tab />
                        say <CodeText type="string">"Code example 1"</CodeText>;
                        <br />
                        <Tab />
                        say <CodeText type="string">"Code example 2"</CodeText>;
                        <br />
                        {"}"} <br />
                        schedule <CodeText type="operator">...</CodeText>{" "}
                        replace {"{"}
                        <br />
                        <Tab />
                        say <CodeText type="string">"Code example 1"</CodeText>;
                        <br />
                        <Tab />
                        say <CodeText type="string">"Code example 2"</CodeText>;
                        <br />
                        {"}"}
                    </CodeBlock>
                </div>
                <section id="execute_expand" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Execute Expand
                    <SectionLinkCopy sectionId="execute_expand" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        Similar to anonymous function, but instead of creating a
                        new function. It'll copy paste execute part of the
                        command instead.
                    </p>
                    <CodeBlock>
                        execute <CodeText type="operator">...</CodeText> expand{" "}
                        {"{"}
                        <br />
                        <Tab />
                        say <CodeText type="string">"Code example 1"</CodeText>;
                        <br />
                        <Tab />
                        say <CodeText type="string">"Code example 2"</CodeText>;
                        <br />
                        {"}"}
                    </CodeBlock>
                    <CodeBlock>
                        execute <CodeText type="operator">...</CodeText> run say{" "}
                        <CodeText type="string">"Code example 1"</CodeText>;
                        <br />
                        execute <CodeText type="operator">...</CodeText> run say{" "}
                        <CodeText type="string">"Code example 2"</CodeText>;
                    </CodeBlock>
                </div>
                <section id="class" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Class (Function grouping)
                    <SectionLinkCopy sectionId="class" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        It simply adds extra layers of directory/namespace to
                        any function/
                        <Link to="/documentation/json-files">json</Link>{" "}
                        (Doesn't affect variable) inside it.{" "}
                        <span className="text-warning">
                            {" "}
                            Even though syntax uses the word "class", it is NOT
                            a traditional class.
                        </span>
                    </p>
                    <CodeBlock>
                        <CodeText type="keyword">class</CodeText>{" "}
                        <CodeText type="class">folder_one.folder_two</CodeText>{" "}
                        <br />
                        {"{"}
                        <br />
                        <Tab />
                        <CodeText type="keyword">function</CodeText>{" "}
                        <CodeText type="function">
                            folder_three.function_name
                        </CodeText>
                        () {"{"}
                        <br />
                        <Tab />
                        <Tab />
                        say <CodeText type="string">"Code example 1"</CodeText>;
                        <br />
                        <Tab />
                        <Tab />
                        say <CodeText type="string">"Code example 2"</CodeText>;
                        <br />
                        <Tab />
                        {"}"}
                        <br />
                        <Tab />
                        <CodeText type="keyword">new</CodeText>{" "}
                        <CodeText type="function">file_type</CodeText>
                        (folder_name.json_file_name) {"{"}{" "}
                        <CodeText type="comment">
                            {"//"} See JSON Files page for this feature
                        </CodeText>
                        <br />
                        <Tab />
                        <Tab />
                        JSON_CONTENT
                        <br /> <Tab />
                        {"}"}
                        <br />
                        {"}"}
                        <br />
                        <br />
                        <CodeText type="function">
                            folder_one.folder_two.folder_three.function_name
                        </CodeText>
                        ();
                    </CodeBlock>
                    <p>
                        <Tab />
                        <code>this</code> keyword can also be used to call
                        function in the same class.
                    </p>
                    <CodeBlock>
                        <CodeText type="keyword">class</CodeText>{" "}
                        <CodeText type="class">folder_one.folder_two</CodeText>{" "}
                        {"{"}
                        <br />
                        <Tab />
                        <CodeText type="function">function_name</CodeText>
                        () {"{}"}
                        <br />
                        <Tab />
                        <CodeText type="function">
                            random_function
                        </CodeText>() {"{"}
                        <br />
                        <Tab />
                        <Tab />
                        <CodeText type="function">function_name</CodeText>
                        ();{" "}
                        <CodeText type="comment">
                            {"// function namespace:function_name"}
                        </CodeText>
                        <br />
                        <Tab />
                        <Tab />
                        <CodeText type="keyword">this</CodeText>
                        <CodeText type="function">.function_name</CodeText>
                        ();{" "}
                        <CodeText type="comment">
                            {
                                "// function namespace:folder_one/folder_two/function_name"
                            }
                        </CodeText>
                        <br />
                        <Tab />
                        {"}"}
                        <br />
                        {"}"}
                    </CodeBlock>
                </div>
                <section id="macro" />
                <div className="text-secondary-contrast text-xl md:text-4xl mt-3 md:mt-4">
                    Vanilla Function Macro (Minecraft 1.20.2+)
                    <SectionLinkCopy sectionId="macro" />
                </div>
                <div className="text-white text-base md:text-2xl mt-4 max-w-full">
                    <p>
                        <Tab />
                        JMC has some special syntaxes to call vanilla macros.
                    </p>
                    <CodeBlock>
                        <CodeText type="function">function_name</CodeText>
                        (key<CodeText type="operator">=</CodeText>
                        <CodeText type="string">"value"</CodeText>);
                        <br />
                        <CodeText type="function">function_name</CodeText>({"{"}
                        <CodeText type="string">"key"</CodeText>
                        <CodeText type="operator">:</CodeText>
                        <CodeText type="string">"value"</CodeText>
                        {"}"});
                        <br />
                        <CodeText type="function">function_name</CodeText>
                        () with <CodeText type="operator">...</CodeText>;
                    </CodeBlock>
                    <code>with</code> works with storage, entity and block too.
                    <CodeBlock>
                        <CodeText type="function">function_name</CodeText>
                        () with {"{"}
                        <CodeText type="string">"key"</CodeText>
                        <CodeText type="operator">:</CodeText>
                        <CodeText type="string">"value"</CodeText>
                        {"}"}
                        ;
                        <br />
                        <CodeText type="function">function_name</CodeText>
                        () with <CodeText type="class">my_storage</CodeText>
                        <CodeText type="operator">::</CodeText>
                        <CodeText type="param">path</CodeText>
                        <CodeText type="operator">.</CodeText>
                        <CodeText type="param">to</CodeText>
                        <CodeText type="operator">.</CodeText>
                        <CodeText type="param">element</CodeText>
                        ;
                        <br />
                        <CodeText type="function">function_name</CodeText>
                        () with <CodeText type="param">@a</CodeText>
                        [tag
                        <CodeText type="operator">=</CodeText>
                        jmc]
                        <CodeText type="operator">::</CodeText>
                        my_path ;
                        <br />
                        <CodeText type="function">function_name</CodeText>
                        () with [<CodeText type="number">~</CodeText>
                        <CodeText type="operator">,</CodeText>{" "}
                        <CodeText type="number">10</CodeText>
                        <CodeText type="operator">,</CodeText>{" "}
                        <CodeText type="number">~</CodeText>]
                        <CodeText type="operator">::</CodeText>;
                    </CodeBlock>
                    <p>
                        It also has a special feature that automatically convert
                        variables into nbt storage for macro usage. Simply use
                        it with a list of variable. Then use <code>$(n)</code>{" "}
                        to access item n, starting with 0.
                    </p>
                    <CodeBlock>
                        execute run {"{"}
                        <br />
                        <Tab />
                        <CodeText type="operator">$</CodeText>tp{" "}
                        <CodeText type="param">@s</CodeText>{" "}
                        <CodeText type="number">$(0) $(1) $(2)</CodeText>;
                        <br />
                        {"}"} with [<CodeText type="variable">$x</CodeText>
                        <CodeText type="operator">,</CodeText>{" "}
                        <CodeText type="variable">$y</CodeText>
                        <CodeText type="operator">,</CodeText>{" "}
                        <CodeText type="variable">$z</CodeText>];
                    </CodeBlock>
                </div>
            </section>
        </>
    );
};

export default Function;
